import React, { useEffect, useMemo, useState } from "react";

import {
  FaSearch,
  FaFilter,
  FaEye,
  FaTrash,
  FaTimes,
  FaEnvelope,
  FaPhoneAlt,
  FaUser,
  FaBuilding,
  FaCube,
  FaTag,
  FaRupeeSign,
  FaCommentAlt,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaInbox,
  FaSpinner,
} from "react-icons/fa";

import "./Inquiry.css";
import { BASE_URL } from "../../Config/config";
export default function Inquiry() {
  /* =====================================================
     API
  ===================================================== */

  const API_URL = `${BASE_URL}/inquiries.php`;

  /* =====================================================
     STATES
  ===================================================== */

  const [search, setSearch] = useState("");

  const [moduleFilter, setModuleFilter] = useState("");

  const [dateFilter, setDateFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState("");

  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);

  const [inquiries, setInquiries] = useState([]);

  const [loading, setLoading] = useState(true);

  const [deleting, setDeleting] = useState(false);

  const itemsPerPage = 20;

  /* =====================================================
     MODULES
  ===================================================== */

  /* =====================================================
     FETCH INQUIRIES
  ===================================================== */

  const fetchInquiries = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_URL}?action=get`);

      const result = await response.json();

      console.log("Inquiry API Response:", result);

      if (result.status) {
        setInquiries(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error(result.message || "Failed to fetch inquiries");

        setInquiries([]);
      }
    } catch (error) {
      console.error("Fetch inquiries error:", error);

      setInquiries([]);
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     INITIAL LOAD
  ===================================================== */

  useEffect(() => {
    fetchInquiries();
  }, []);

  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "-";
    }

    const date = new Date(dateValue.replace(" ", "T"));

    if (isNaN(date.getTime())) {
      return dateValue;
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /* =====================================================
     FILTER
  ===================================================== */

  const filteredInquiries = useMemo(() => {
    return inquiries.filter((item) => {
      const searchText = search.toLowerCase().trim();

      const fullName = String(item.fullName || "").toLowerCase();

      const email = String(item.email || "").toLowerCase();

      const phone = String(item.phone || "").toLowerCase();

      const businessName = String(item.businessName || "").toLowerCase();

      const module = String(item.module || "").toLowerCase();

      const moduleCode = String(item.moduleCode || "").toLowerCase();

      const matchesSearch =
        !searchText ||
        fullName.includes(searchText) ||
        email.includes(searchText) ||
        phone.includes(searchText) ||
        businessName.includes(searchText) ||
        module.includes(searchText) ||
        moduleCode.includes(searchText);

      const matchesModule = !moduleFilter || item.module === moduleFilter;

      const matchesStatus = !statusFilter || item.status === statusFilter;

      let matchesDate = true;

      if (dateFilter) {
        const createdAt = String(item.createdAt || "");

        matchesDate = createdAt.startsWith(dateFilter);
      }

      return matchesSearch && matchesModule && matchesStatus && matchesDate;
    });
  }, [inquiries, search, moduleFilter, statusFilter, dateFilter]);

  /* =====================================================
     PAGINATION
  ===================================================== */

  const totalPages = Math.ceil(filteredInquiries.length / itemsPerPage);

  const paginatedInquiries = filteredInquiries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  /* =====================================================
     DELETE INQUIRY
  ===================================================== */

  const deleteInquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) {
      return;
    }

    try {
      setDeleting(true);

      const response = await fetch(`${API_URL}?action=delete&id=${id}`, {
        method: "DELETE",
      });

      const result = await response.json();

      console.log("Delete Inquiry Response:", result);

      if (!result.status) {
        alert(result.message || "Failed to delete inquiry");
        return;
      }

      setInquiries((prev) =>
        prev.filter((item) => Number(item.id) !== Number(id)),
      );

      if (selectedInquiry && Number(selectedInquiry.id) === Number(id)) {
        setSelectedInquiry(null);
      }

      /* Fix page if last item was deleted */

      setCurrentPage((prevPage) => {
        const newTotal = Math.ceil(
          (filteredInquiries.length - 1) / itemsPerPage,
        );

        if (newTotal <= 0) {
          return 1;
        }

        return Math.min(prevPage, newTotal);
      });

      alert("Inquiry deleted successfully");
    } catch (error) {
      console.error("Delete inquiry error:", error);

      alert("Something went wrong while deleting inquiry");
    } finally {
      setDeleting(false);
    }
  };

  /* =====================================================
     RESET FILTERS
  ===================================================== */

  const resetFilters = () => {
    setSearch("");

    setModuleFilter("");

    setDateFilter("");

    setStatusFilter("");

    setCurrentPage(1);
  };

  /* =====================================================
     STATUS CLASS
  ===================================================== */

  /* =====================================================
     STATUS COUNTS
  ===================================================== */
  const getPaginationPages = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }

    if (currentPage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  /* =====================================================
     RETURN
  ===================================================== */

  return (
    <div className="admin-inquiry-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="admin-page-header">
        <div>
          <span className="admin-page-label">INQUIRY MANAGEMENT</span>

          <h1>Customer Inquiries</h1>

          <p>View and manage all enquiries received from the website.</p>
        </div>

        <div className="inquiry-total-box">
          <FaInbox />

          <div>
            <strong>{filteredInquiries.length}</strong>

            <span>Total Inquiries</span>
          </div>
        </div>
      </div>

      <div className="inquiry-filter-box">
        <div className="inquiry-filter-title">
          <FaFilter />

          <span>Filter Inquiries</span>
        </div>

        <div className="inquiry-filters">
          {/* SEARCH */}

          <div className="inquiry-filter search">
            <label>Search</label>

            <div className="inquiry-input">
              <FaSearch />

              <input
                type="text"
                placeholder="Name, email, phone, business..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* MODULE */}

          {/* STATUS */}

          {/* DATE */}

          <div className="inquiry-filter">
            <label>Date</label>

            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* RESET */}

          <button
            type="button"
            className="inquiry-reset-btn"
            onClick={resetFilters}
          >
            <FaTimes />
            Reset
          </button>
        </div>
      </div>

      {/* =====================================================
          TABLE
      ===================================================== */}

      <div className="inquiry-table-box">
        <div className="inquiry-table-header">
          <div>
            <span>INQUIRIES</span>

            <h2>Customer Enquiries</h2>
          </div>

          <div className="inquiry-result-count">
            Showing <strong>{paginatedInquiries.length}</strong> of{" "}
            <strong>{filteredInquiries.length}</strong>
          </div>
        </div>

        {/* =====================================================
            LOADING
        ===================================================== */}

        {loading ? (
          <div className="inquiry-empty">
            <FaSpinner className="inquiry-loading-spinner" />

            <h3>Loading Inquiries...</h3>

            <p>Please wait while we load customer enquiries.</p>
          </div>
        ) : paginatedInquiries.length > 0 ? (
          <div className="inquiry-table-wrapper">
            <table className="inquiry-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>

                  <th>Customer</th>

                  <th>Business</th>

                  <th>Module</th>

                  <th>Message</th>

                  {/* <th>Status</th> */}

                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedInquiries.map((item, index) => (
                  <tr key={item.id}>
                    {/* NUMBER */}

                    <td>
                      <span className="inquiry-number">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </span>
                    </td>

                    {/* CUSTOMER */}
                    <td>
                      <div className="inquiry-date">
                        <FaCalendarAlt />

                        <span>{formatDate(item.createdAt)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="inquiry-customer">
                        <div className="customer-avatar">
                          <FaUser />
                        </div>

                        <div>
                          <strong>{item.fullName || "-"}</strong>

                          <span>{item.email || "-"}</span>

                          <small>{item.phone || "-"}</small>
                        </div>
                      </div>
                    </td>

                    {/* BUSINESS */}

                    <td>
                      <div className="inquiry-business">
                        <FaBuilding />

                        <span>{item.businessName || "-"}</span>
                      </div>
                    </td>

                    {/* MODULE */}

                    <td>
                      <div className="inquiry-module">
                        <strong>{item.module || "-"}</strong>

                        <span>{item.moduleCode || "-"}</span>

                        <small>{item.modulePrice || "-"}</small>
                      </div>
                    </td>

                    {/* MESSAGE */}

                    <td>
                      <div className="inquiry-message">
                        <FaCommentAlt />

                        <p>{item.message || "-"}</p>
                      </div>
                    </td>

                    {/* STATUS */}

                    {/* <td>
                      <span className={getStatusClass(item.status)}>
                        {item.status || "New"}
                      </span>
                    </td> */}

                    {/* DATE */}

                    {/* ACTION */}

                    <td>
                      <div className="inquiry-actions">
                        {/* VIEW */}

                        <button
                          type="button"
                          title="View Inquiry"
                          onClick={() => setSelectedInquiry(item)}
                        >
                          <FaEye />
                        </button>

                        {/* DELETE */}

                        <button
                          type="button"
                          className="danger"
                          title="Delete Inquiry"
                          disabled={deleting}
                          onClick={() => deleteInquiry(item.id)}
                        >
                          {deleting ? (
                            <FaSpinner className="inquiry-loading-spinner" />
                          ) : (
                            <FaTrash />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* =====================================================
             EMPTY
          ===================================================== */

          <div className="inquiry-empty">
            <FaInbox />

            <h3>No Inquiries Found</h3>

            <p>Try changing your search or filter options.</p>
          </div>
        )}

        {/* =====================================================
            PAGINATION
        ===================================================== */}

        {!loading && totalPages > 1 && (
          <div className="inquiry-pagination">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              <FaChevronLeft />
            </button>

            {getPaginationPages().map((page, index) =>
              page === "..." ? (
                <span key={`dots-${index}`} className="pagination-dots">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  className={currentPage === page ? "active" : ""}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ),
            )}

            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* =====================================================
          VIEW INQUIRY MODAL
      ===================================================== */}

      {selectedInquiry && (
        <div
          className="inquiry-modal-overlay"
          onClick={() => setSelectedInquiry(null)}
        >
          <div className="inquiry-modal" onClick={(e) => e.stopPropagation()}>
            {/* HEADER */}

            <div className="inquiry-modal-header">
              <div>
                <span>INQUIRY DETAILS</span>

                <h2>{selectedInquiry.fullName}</h2>
              </div>

              <button type="button" onClick={() => setSelectedInquiry(null)}>
                <FaTimes />
              </button>
            </div>

            {/* BODY */}

            <div className="inquiry-modal-body">
              {/* =================================================
                  CUSTOMER
              ================================================= */}

              <div className="inquiry-detail-section">
                <h3>Customer Information</h3>

                <div className="inquiry-detail-grid">
                  {/* NAME */}

                  <div className="inquiry-detail">
                    <FaUser />

                    <div>
                      <span>Full Name</span>

                      <strong>{selectedInquiry.fullName || "-"}</strong>
                    </div>
                  </div>

                  {/* EMAIL */}

                  <div className="inquiry-detail">
                    <FaEnvelope />

                    <div>
                      <span>Email</span>

                      <strong>{selectedInquiry.email || "-"}</strong>
                    </div>
                  </div>

                  {/* PHONE */}

                  <div className="inquiry-detail">
                    <FaPhoneAlt />

                    <div>
                      <span>Phone</span>

                      <strong>{selectedInquiry.phone || "-"}</strong>
                    </div>
                  </div>

                  {/* BUSINESS */}

                  <div className="inquiry-detail">
                    <FaBuilding />

                    <div>
                      <span>Business Name</span>

                      <strong>{selectedInquiry.businessName || "-"}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* =================================================
                  MODULE
              ================================================= */}

              <div className="inquiry-detail-section">
                <h3>Module Information</h3>

                <div className="inquiry-detail-grid">
                  {/* MODULE */}

                  <div className="inquiry-detail">
                    <FaCube />

                    <div>
                      <span>Module</span>

                      <strong>{selectedInquiry.module || "-"}</strong>
                    </div>
                  </div>

                  {/* CODE */}

                  <div className="inquiry-detail">
                    <FaTag />

                    <div>
                      <span>Module Code</span>

                      <strong>{selectedInquiry.moduleCode || "-"}</strong>
                    </div>
                  </div>

                  {/* PRICE */}

                  <div className="inquiry-detail">
                    <FaRupeeSign />

                    <div>
                      <span>Price</span>

                      <strong>{selectedInquiry.modulePrice || "-"}</strong>
                    </div>
                  </div>

                  {/* DATE */}

                  <div className="inquiry-detail">
                    <FaCalendarAlt />

                    <div>
                      <span>Received</span>

                      <strong>{formatDate(selectedInquiry.createdAt)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* =================================================
                  STATUS
              ================================================= */}

              <div className="inquiry-detail-section">
                <h3>Inquiry Status</h3>

                <div className="inquiry-detail-grid">
                  <div className="inquiry-detail">
                    <FaInbox />

                    <div>
                      <span>Status</span>

                      <strong>{selectedInquiry.status || "New"}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* =================================================
                  MESSAGE
              ================================================= */}

              <div className="inquiry-message-detail">
                <h3>Customer Message</h3>

                <p>{selectedInquiry.message || "No message provided."}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
